import { supabase } from '../utils/supabase';

/**
 * Referral Service
 * Handles referral program operations
 */
class ReferralService {
  /**
   * Get user's referral stats
   */
  async getUserReferralStats(userId) {
    try {
      const { data, error } = await supabase
        .from('user_referrals')
        .select('*')
        .eq('referrer_user_id', userId);

      if (error) {
        // Table might not exist yet
        if (error.code === 'PGRST116' || error.message.includes('does not exist')) {
          return { success: true, data: [] };
        }
        throw error;
      }

      const totalReferrals = data?.length || 0;
      const completedReferrals = data?.filter(r => r.status === 'completed').length || 0;
      const pendingReferrals = data?.filter(r => r.status === 'pending').length || 0;
      const totalRewards = data?.reduce((sum, r) => sum + (r.reward_amount || 0), 0) || 0;

      // Determine tier based on completed referrals
      let currentTier = 'Bronze';
      let nextTier = 'Silver';
      let pointsToNextTier = 3 - completedReferrals;

      if (completedReferrals >= 11) {
        currentTier = 'Platinum';
        nextTier = 'Platinum';
        pointsToNextTier = 0;
      } else if (completedReferrals >= 6) {
        currentTier = 'Gold';
        nextTier = 'Platinum';
        pointsToNextTier = 11 - completedReferrals;
      } else if (completedReferrals >= 3) {
        currentTier = 'Silver';
        nextTier = 'Gold';
        pointsToNextTier = 6 - completedReferrals;
      }

      return {
        success: true,
        data: {
          totalReferrals,
          pendingReferrals,
          completedReferrals,
          totalRewards,
          currentTier,
          nextTier,
          pointsToNextTier,
          referrals: data
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get user's referral code
   */
  async getUserReferralCode(userId) {
    try {
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('referral_code, email, full_name')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching referral code:', error);
        return { success: false, error: error.message };
      }
      
      if (!profile) {
        return { success: false, error: 'User profile not found' };
      }

      // Generate referral code if doesn't exist
      if (!profile.referral_code) {
        const code = this.generateReferralCode(profile.email, profile.full_name);
        
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({ referral_code: code })
          .eq('id', userId);

        if (updateError) throw updateError;

        return {
          success: true,
          data: { referralCode: code }
        };
      }

      return {
        success: true,
        data: { referralCode: profile.referral_code }
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Generate a unique referral code
   */
  generateReferralCode(email, fullName) {
    const namePart = (fullName || email.split('@')[0]).replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 4);
    const randomPart = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `${namePart}${randomPart}`;
  }

  /**
   * Create a referral
   */
  async createReferral(referrerUserId, referredEmail, referralCode) {
    try {
      const { data, error } = await supabase
        .from('user_referrals')
        .insert({
          referrer_user_id: referrerUserId,
          referred_email: referredEmail,
          referral_code: referralCode,
          status: 'pending',
          reward_amount: 25
        })
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
        data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get referral history
   */
  async getReferralHistory(userId, { limit = 50, offset = 0 } = {}) {
    try {
      const { data, error, count } = await supabase
        .from('user_referrals')
        .select('*, referred_user:user_profiles!referred_user_id(full_name, email)', { count: 'exact' })
        .eq('referrer_user_id', userId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) throw error;

      return {
        success: true,
        data,
        count
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Redeem referral rewards
   */
  async redeemRewards(userId, amount) {
    try {
      // Get available rewards
      const { data: referrals, error: fetchError } = await supabase
        .from('user_referrals')
        .select('id, reward_amount, is_redeemed')
        .eq('referrer_user_id', userId)
        .eq('status', 'completed')
        .eq('is_redeemed', false);

      if (fetchError) throw fetchError;

      const availableAmount = referrals.reduce((sum, r) => sum + r.reward_amount, 0);

      if (availableAmount < amount) {
        return {
          success: false,
          error: 'Insufficient rewards available'
        };
      }

      // Mark referrals as redeemed
      let remaining = amount;
      const toRedeem = [];

      for (const referral of referrals) {
        if (remaining <= 0) break;
        
        if (referral.reward_amount <= remaining) {
          toRedeem.push(referral.id);
          remaining -= referral.reward_amount;
        }
      }

      const { error: updateError } = await supabase
        .from('user_referrals')
        .update({ is_redeemed: true, redeemed_at: new Date().toISOString() })
        .in('id', toRedeem);

      if (updateError) throw updateError;

      return {
        success: true,
        data: {
          redeemedAmount: amount,
          remainingRewards: availableAmount - amount
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }
}

export default new ReferralService();
